import { Card, Col, Row } from "reactstrap";
import { dynamicAPIImage } from "../../../Utils";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { H6, Image, P } from "../../../AbstractElements";
import CommonCardHeader from "../../CommonComponents/CommonCardHeader/CommonCardHeader";
import { Link } from "react-router-dom";
import { Camera } from "react-feather";

//SERVICES
import {
  VIEW_EMPLOYEE_PROFILE_QUERY_KEY,
  employeeService,
} from "../../../Services/employeeService";

//TYPES
import { CRUDPermissions } from "../../../Types/Common/Access";
import { ViewEmployeeProfile } from "../../../Types/Employee/EmployeeModel";

//MODALS
import UploadProfileModal from "../../UiKits/Modal/Employee/UpdateProfilePic";

interface EmployeeProfileProps {
  EmpNo: string;
  access: CRUDPermissions;
}

const EmployeeProfile: React.FC<EmployeeProfileProps> = ({ EmpNo, access }) => {
  const [formData, setFormData] = useState<ViewEmployeeProfile | null>(null);
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);

  const { data: responseData, isLoading: isFetching } = useQuery({
    queryKey: [VIEW_EMPLOYEE_PROFILE_QUERY_KEY, EmpNo],
    queryFn: () => employeeService.viewEmployeeProfileByEmpno(EmpNo),
    enabled: !!EmpNo,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    if (responseData?.data) {
      setFormData(responseData?.data);
    }
  }, [responseData?.data]);

  const getEmploymentStatusColor = (
    status: number | string | null | undefined,
  ) => {
    switch (Number(status)) {
      case 1:
        return "warning";
      case 4:
      case 5:
      case 6:
      case 7:
        return "danger";

      case 3:
        return "info";

      case 2:
      default:
        return "primary";
    }
  };

  const fullName = [
    formData?.first_name,
    formData?.middle_name,
    formData?.last_name,
    formData?.suffix,
  ]
    .filter(Boolean)
    .join(" ");

  const fullAddress = [
    formData?.address_line1,
    formData?.address_line2,
    formData?.barangay,
    formData?.city,
    formData?.province,
    formData?.country,
    formData?.zip_code,
  ]
    .map((value) => value?.toString().trim())
    .filter(Boolean)
    .join(", ");

  const mobileNo = [formData?.mobile1, formData?.mobile2]
    .map((value) => value?.toString().trim())
    .filter(Boolean)
    .join(" | ");

  return (
    <Col sm="12">
      {" "}
      {/* START COL */}
      {/* MODALS */}
      <UploadProfileModal EmpNo={EmpNo} modal={modal} toggle={toggle} />
      {/* HEAHER CARD */}
      <Card className="hovercard text-center user-profile-card">
        <CommonCardHeader
          headClass={`bg-${getEmploymentStatusColor(
            formData?.employment_status,
          )} d-flex align-items-center pb-3`}
          tagClass="txt-light"
          icon={<i className="icofont icofont-warning-alt me-2"></i>}
          title={
            formData?.employment_status_label && formData?.employment_type_label
              ? `${formData.employment_status_label} • ${formData.employment_type_label}`
              : "Loading..."
          }
        />
        <div className="cardheader"></div>
        <div className="user-image">
          <div className="avatar">
            <Image
              className="img-fluid top-radius-blog"
              src={dynamicAPIImage(formData?.profile_url)}
              alt={formData?.application_no}
              style={{ width: 200, height: 200, objectFit: "cover" }}
            />
            <br></br>
            <div
              className="icon-wrapper"
              onClick={toggle}
              hidden={!access.update}
            >
              <Camera size={16} />
            </div>
          </div>
        </div>
        <div className="info">
          <Row className="g-3">
            {/* LEFT */}
            <Col sm="6" lg="4" className="order-sm-1 order-xl-0">
              <Row>
                <Col md="7">
                  <div className="ttl-info text-start">
                    <H6 className="mb-1">
                      <i className="fa fa-envelope me-2"></i> Email Address
                    </H6>
                    <span className="mb-2">
                      {isFetching
                        ? "Loading data..."
                        : formData?.email_address
                          ? formData.email_address
                          : "* No Record Found *"}
                    </span>
                  </div>
                </Col>
                <Col md="5">
                  <div className="ttl-info text-start">
                    <H6 className="mb-1">
                      <i className="fa fa-calendar me-2"></i> Date of Birth
                    </H6>
                    <span className="mb-2">
                      {isFetching
                        ? "Loading data..."
                        : formData?.birthdate
                          ? `${new Date(formData.birthdate).toLocaleDateString(
                              "en-GB",
                              {
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                              },
                            )} (${formData.age})`
                          : "* No Record Found *"}
                    </span>
                  </div>
                </Col>
              </Row>
            </Col>
            {/* CENTER */}
            <Col sm="12" lg="4" className="order-sm-0 order-xl-1">
              <div className="user-designation">
                <div className="title">
                  <br></br>
                  <Link to="#">
                    <b>{fullName?.toUpperCase()}</b>
                  </Link>
                </div>
                <P>
                  {formData?.job_title?.toUpperCase() || "* No Record Found *"}
                  <br />
                  {formData?.hired_date_human
                    ? formData.hired_date_human
                    : "* No Record Found *"}
                </P>
              </div>
            </Col>
            {/* RIGHT */}
            <Col sm="6" lg="4" className="order-sm-2 order-xl-2">
              <Row>
                <Col md="7">
                  <div className="ttl-info text-start">
                    <H6 className="mb-1">
                      <i className="fa fa-phone me-2"></i> Contact No.
                    </H6>
                    <span className="mb-2">
                      {isFetching
                        ? "Loading data..."
                        : mobileNo
                          ? mobileNo
                          : "* No Record Found *"}
                    </span>
                  </div>
                </Col>
                <Col md="5">
                  <div className="ttl-info text-start">
                    <H6 className="mb-1">
                      <i className="fa fa-location-arrow me-2"></i> Address
                    </H6>
                    <span className="mb-2">
                      {isFetching
                        ? "Loading data..."
                        : fullAddress
                          ? fullAddress
                          : "* No Record Found *"}
                    </span>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </Card>
      {/* END HEAHER CARD */}
    </Col>
  );
};

export default EmployeeProfile;
